import {useEffect, useRef} from 'react';
import {COMPLETE_BUTTON_MODES} from '../../../utils/gameUi/getCompleteButtonView';
import {REGISTRY_SINGLETON} from '../../../scenes/Registry';
import {checkIfHotkeyBelongsToPage} from '../../../utils/unitAutoSelection/hotkeyTargets';

// Сколько после перехода кнопки в «готово» Enter еще не завершает этап: пробел
// пропустил ход последним отрядом, кнопка загорелась, а игрок по инерции жмет
// Enter, собираясь сделать что-то еще
export const ENTER_READY_DELAY_MS = 500;

// Режимы, в которых Enter завершает этап; в «есть дела» он показывает
// предупреждение фазы
const COMPLETING_MODES = [COMPLETE_BUTTON_MODES.READY, COMPLETE_BUTTON_MODES.CONFIRM];
const HANDLED_MODES = [...COMPLETING_MODES, COMPLETE_BUTTON_MODES.PENDING];

export const checkIfEnterKeyHandledMode = (mode) => HANDLED_MODES.includes(mode);

const checkIfFieldAnimating = () => Boolean(REGISTRY_SINGLETON.getHexGridRenderer()?.isFieldActionAnimating?.());

/**
 * Enter на кнопке хода (spec unit-auto-selection, «Завершение этапа клавишей
 * Enter»): в «готово» и на неподтвержденном ближнем бою — onComplete, пока дела
 * есть — onWarn. Отдельная клавиша, а не пробел: пробел жмут подряд, пролистывая
 * отряды, и лишнее нажатие завершило бы этап.
 *
 * Фаза захвата и preventDefault: иначе Enter нажал бы кнопку в фокусе — в том
 * числе саму кнопку хода, которая после клика остается в фокусе, и этап
 * завершился бы мимо предупреждения.
 *
 * @param {string} mode — режим кнопки (getCompleteButtonView)
 * @param {boolean} isEnabled — свой ход, не обучение и не просмотр
 * @param {string} stageKey — меняется на каждом этапе: «готово» следующего
 *  этапа снова выжидает ENTER_READY_DELAY_MS
 */
export const useCompletePhaseEnterKey = ({mode, isEnabled, stageKey, onComplete, onWarn}) => {
  const isCompletingMode = COMPLETING_MODES.includes(mode);
  const readySinceRef = useRef(0);
  useEffect(() => {
    if (isCompletingMode) {
      readySinceRef.current = Date.now();
    }
  }, [isCompletingMode, stageKey]);

  const latestRef = useRef(null);
  latestRef.current = {mode, isEnabled, onComplete, onWarn};

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Enter') {
        return;
      }
      const latest = latestRef.current;
      if (!latest.isEnabled || !HANDLED_MODES.includes(latest.mode)
        || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey
        || checkIfHotkeyBelongsToPage(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (event.repeat || checkIfFieldAnimating()) {
        return;
      }
      if (!COMPLETING_MODES.includes(latest.mode)) {
        latest.onWarn();
        return;
      }
      if (Date.now() - readySinceRef.current >= ENTER_READY_DELAY_MS) {
        latest.onComplete();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);
};
